package fr.frux.feature.map

import androidx.compose.foundation.layout.Arrangement
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.PaddingValues
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.lazy.LazyRow
import androidx.compose.foundation.lazy.items
import androidx.compose.material3.MaterialTheme
import androidx.compose.material3.Text
import androidx.compose.runtime.Composable
import androidx.compose.ui.Modifier
import androidx.compose.ui.semantics.contentDescription
import androidx.compose.ui.semantics.semantics
import fr.frux.core.designsystem.components.FruxActionChip
import fr.frux.core.designsystem.theme.FruxSpacing
import fr.frux.feature.map.resources.Res
import fr.frux.feature.map.resources.map_filters_section_season_highlights
import fr.frux.feature.map.resources.map_seasonal_chip_description_with_count_plural
import fr.frux.feature.map.resources.map_seasonal_chip_description_with_count_singular
import fr.frux.feature.map.resources.map_seasonal_chip_description_without_count
import fr.frux.feature.map.resources.map_seasonal_chip_with_count
import fr.frux.feature.map.resources.map_seasonal_next_heading
import fr.frux.feature.map.resources.map_seasonal_subtitle_local
import fr.frux.feature.map.resources.map_seasonal_subtitle_national
import fr.frux.shared.domain.model.SeasonalAround
import fr.frux.shared.domain.model.SeasonalSpecies
import fr.frux.shared.domain.model.Species
import org.jetbrains.compose.resources.stringResource

/**
 * Mise en avant « de saison » commune a la carte et aux recoltes (#403, ADR 0020) : pas d'ecran
 * dedie, seulement une section reutilisable au-dessus d'une liste existante. Vit dans
 * `feature-map` — pas `core-designsystem`, qui ne peut pas voir `:shared` (CLAUDE.md, règle 4) —
 * et composeApp le consomme comme n'importe quelle autre feature en aval.
 *
 * @param onSpeciesClick Tap sur une pastille : l'appelant decide de la suite (carte filtree,
 *   navigation).
 * @param showScope Affiche la portee (zone de la carte ou France entiere) sous le premier titre.
 *   La feuille « Autour de toi » la masque en local, son propre titre la dit deja.
 */
@Composable
fun SeasonalAroundSection(
    data: SeasonalAround,
    onSpeciesClick: (Species) -> Unit,
    modifier: Modifier = Modifier,
    showScope: Boolean = true,
    /** Titre de la rangee « maintenant », a la place de « De saison en ce moment » (#502). */
    heading: String? = null,
) {
    if (data.now.isEmpty() && data.next.isEmpty()) return
    val scope = if (showScope) subtitleFor(data.isLocal) else null

    Column(modifier = modifier) {
        if (data.now.isNotEmpty()) {
            SeasonalRow(
                heading = heading ?: stringResource(Res.string.map_filters_section_season_highlights),
                subtitle = scope,
                items = data.now,
                onSpeciesClick = onSpeciesClick,
            )
        }
        if (data.next.isNotEmpty()) {
            SeasonalRow(
                heading = stringResource(Res.string.map_seasonal_next_heading),
                subtitle = scope.takeIf { data.now.isEmpty() },
                items = data.next,
                onSpeciesClick = onSpeciesClick,
            )
        }
    }
}

@Composable
private fun subtitleFor(isLocal: Boolean): String =
    stringResource(
        if (isLocal) Res.string.map_seasonal_subtitle_local else Res.string.map_seasonal_subtitle_national,
    )

@Composable
private fun SeasonalRow(
    heading: String,
    subtitle: String?,
    items: List<SeasonalSpecies>,
    onSpeciesClick: (Species) -> Unit,
) {
    Column {
        Text(
            text = heading,
            style = MaterialTheme.typography.labelLarge,
            color = MaterialTheme.colorScheme.onSurfaceVariant,
        )
        if (subtitle != null) {
            Text(
                text = subtitle,
                style = MaterialTheme.typography.bodySmall,
                color = MaterialTheme.colorScheme.onSurfaceVariant,
                modifier = Modifier.padding(bottom = FruxSpacing.xs),
            )
        }
        LazyRow(
            contentPadding = PaddingValues(vertical = FruxSpacing.xs),
            horizontalArrangement = Arrangement.spacedBy(FruxSpacing.sm),
        ) {
            items(items, key = { it.species.id }) { item ->
                val label =
                    item.treeCount?.let { count ->
                        stringResource(Res.string.map_seasonal_chip_with_count, item.species.commonNameFr, count)
                    } ?: item.species.commonNameFr
                val description =
                    item.treeCount?.let { count ->
                        if (count == 1) {
                            stringResource(
                                Res.string.map_seasonal_chip_description_with_count_singular,
                                item.species.commonNameFr,
                                count,
                            )
                        } else {
                            stringResource(
                                Res.string.map_seasonal_chip_description_with_count_plural,
                                item.species.commonNameFr,
                                count,
                            )
                        }
                    }
                        ?: stringResource(
                            Res.string.map_seasonal_chip_description_without_count,
                            item.species.commonNameFr,
                        )

                FruxActionChip(
                    label = label,
                    onClick = { onSpeciesClick(item.species) },
                    modifier = Modifier.semantics { contentDescription = description },
                )
            }
        }
    }
}
